import React, { useMemo } from 'react';
import { ClientPhotoSize } from '@common/const/client/photo';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { ImageAssetItem } from '@common/model/resource/image';
import { ClientPhotoViewModel } from '@common/model/client/photo/view';
import { ResumeCardNew as BaseResumeCard } from '@cp/entities/resume';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { AttachmentType } from '@common/model/resource/attachment/type';
import { Image as Img } from '@youhelp-ru/ui';
import { getResizedAssetItem, getResizedAssetItems } from '@cp/shared/image';
import { CatalogItemPrice } from 'src/entities/catalog/ui/price.new';
import { CatalogItemResumeCardProps } from './model';
import { Achievement } from './achievementNew';
import { CARD_CONTAINER_SX } from './const';
import { EmptyPhoto } from './EmptyPhoto';

export * from './const';
export * from './model';

const mainPhotoToAsset = (photo: ClientPhotoViewModel, size: number): ImageAssetItem => {
  const resizedAsset = getResizedAssetItem(photo.image.base, size);
  return resizedAsset || { src: photo.notCroppedOriginalSrc as string, width: size, height: size };
};

export const ResumeCardNew: React.FC<CatalogItemResumeCardProps> = ({
  width = '100%',
  data,
  header,
  sx,
  onClick,
  component,
  loadPolicy = 'lazy',
  clientProfilePhotoPurpose = ClientPhotoPurpose.Avatar,
}) => {
  const mergedSx = React.useMemo(() => mergeSx(CARD_CONTAINER_SX, sx), [sx]);

  const { mainAsset, assets } = useMemo(() => {
    const clientPhoto =
      clientProfilePhotoPurpose === ClientPhotoPurpose.Resume
        ? (data.clientProfile?.photoSet?.find((p) => p.purpose === clientProfilePhotoPurpose) ?? data.clientProfile?.mainPhoto)
        : (data.clientProfile?.photoSet?.find((p) => p.purpose === clientProfilePhotoPurpose) ?? data.clientProfile?.mainPhoto);
    if (!clientPhoto) {
      return { assets: [] as ReturnType<typeof getResizedAssetItems> };
    }
    return {
      assets: getResizedAssetItems(clientPhoto.image, ClientPhotoSize.Medium),
      mainAsset: mainPhotoToAsset(clientPhoto, ClientPhotoSize.Medium),
    };
  }, [clientProfilePhotoPurpose, data.clientProfile?.mainPhoto, data.clientProfile?.photoSet]);

  return (
    <BaseResumeCard
      achievements={data.clientProfile?.isOnline && <Achievement type="online" />}
      component={component}
      data={data}
      data-itemid={data.id}
      header={header}
      itemPrice={<CatalogItemPrice data={data} />}
      mediaButton={Boolean(data.clientProfile?.hasAudio || data.media.some((item) => item.type === AttachmentType.Audio))}
      onClick={onClick}
      profilePhoto={
        mainAsset ? (
          <picture>
            {assets.map((asset) => (
              <source key={asset.src} srcSet={asset.src} type={asset.mimeType} />
            ))}
            <Img
              alt={[data.clientProfile?.name, data.clientProfile?.surname].filter(Boolean).join(' ')}
              height={mainAsset.height}
              loading={loadPolicy}
              src={mainAsset.src}
              sx={{ maxWidth: '100%', height: 'auto', display: 'block', margin: 'auto' }}
              width={mainAsset.width}
            />
          </picture>
        ) : (
          <EmptyPhoto alt={[data.clientProfile?.name, data.clientProfile?.surname].filter(Boolean).join(' ')} />
        )
      }
      sx={mergedSx}
      width={width}
    />
  );
};

export default ResumeCardNew;
